import { useMemo, useRef } from 'react';
import { PreparationLineWithProduct } from './usePreparationQuery';

/**
 * Hook que mantiene referencias estables de productos para evitar re-renders innecesarios
 * Solo actualiza las referencias cuando hay cambios reales en los datos
 */
export const useStableProducts = (products: PreparationLineWithProduct[]) => {
  const previousProductsRef = useRef<PreparationLineWithProduct[]>([]);
  const stableProductsRef = useRef<PreparationLineWithProduct[]>([]);

  const stableProducts = useMemo(() => {
    if (!products || products.length === 0) {
      stableProductsRef.current = [];
      return [];
    }

    const previous = previousProductsRef.current;

    // Si es la primera vez o cambió la cantidad de productos, usar los nuevos
    if (previous.length === 0 || previous.length !== products.length) {
      const newProducts = [...products];
      previousProductsRef.current = newProducts;
      stableProductsRef.current = newProducts;
      return newProducts;
    }

    // Crear un mapa de productos anteriores por ID para búsqueda rápida
    const previousMap = new Map(previous.map((p) => [p.id, p]));
    const updatedProducts: PreparationLineWithProduct[] = [];
    let hasChanges = false;

    // Verificar cada producto actual
    for (const currentProduct of products) {
      const previousProduct = previousMap.get(currentProduct.id);

      if (!previousProduct) {
        // Producto nuevo
        updatedProducts.push(currentProduct);
        hasChanges = true;
      } else {
        // Verificar si el producto cambió significativamente
        const hasSignificantChange =
          previousProduct.qty !== currentProduct.qty ||
          previousProduct.qty_done !== currentProduct.qty_done ||
          previousProduct.product_id !== currentProduct.product_id ||
          previousProduct.product?.name !== currentProduct.product?.name ||
          previousProduct.product?.code !== currentProduct.product?.code ||
          previousProduct.product?.image !== currentProduct.product?.image ||
          JSON.stringify(previousProduct.suggested_locations) !== JSON.stringify(currentProduct.suggested_locations) ||
          JSON.stringify(previousProduct.other_locations) !== JSON.stringify(currentProduct.other_locations);

        if (hasSignificantChange) {
          // Usar el producto actualizado
          updatedProducts.push(currentProduct);
          hasChanges = true;
        } else {
          // Mantener la referencia anterior para evitar re-renders
          updatedProducts.push(previousProduct);
        }
      }
    }

    // Solo actualizar las referencias si hay cambios reales
    if (hasChanges) {
      previousProductsRef.current = [...products];
      stableProductsRef.current = updatedProducts;
      return updatedProducts;
    }

    // No hay cambios, devolver la referencia estable anterior
    return stableProductsRef.current;
  }, [products]);

  return stableProducts;
};
